/**
 * What a merged document says about its members, for a caller judging a
 * DOWNLOAD rather than editing (VE-31438).
 *
 * The editor already answers this from the store: it has the project's record,
 * its membership and every open document's page count. The two surfaces that
 * gate a download — the folder row's menu and the Download Review modal — have
 * none of that. They ran the per-file check alone and released members whose
 * numbering or marking the set had moved on from, with nothing on screen
 * saying so.
 *
 * SO THE WHOLE CONTEXT COMES OUT OF TWO `details` BLOBS, in one read: the
 * project TDO's own (membership, and each member's cached page count) and the
 * redaction asset's (the two stamps, and the Bates settings). That is the
 * point of both — `memberInfo` exists so a caller need not load every member's
 * TDO to know the page spans, and the asset's `details` exists so a caller
 * need not download the body to know what the set currently carries.
 *
 * ONE FACT COMES FROM THE CALLER: which members nobody has worked on, and so
 * span no Bates numbers (VE-32395). That is in the request's file rows, not
 * in either blob — see `excludingFromBates`.
 *
 * Each identity keeps the three values the verdicts expect: `undefined` has
 * not been read, `null` is nothing of the set's reaching a member, and a
 * string is the stamp an output must carry. Collapsing the first two reports
 * every member current while the read is outstanding.
 */
import type { TDOId } from '@common-modules/universal/models/Brands';

import { effectiveBatesStarts, type BatesSpanMember } from './batesStamp';
import { parseProjectDetails, projectMemberPageCount } from './models';
import { parseProjectRedactionAssetDetails } from './projectRedactionAsset';

export interface MergedSetStaleness {
  /** The set's members, or undefined while the membership is unread. */
  readonly members: ReadonlySet<string> | undefined;
  readonly batesModifiedDateTime: string | null | undefined;
  readonly watermarksModifiedDateTime: string | null | undefined;
  /** Each member's expected first Bates number, by TDO. */
  readonly expectedStartById: ReadonlyMap<string, number | null> | undefined;
  /**
   * What `expectedStartById` was computed from, kept so it can be computed
   * again with the members the merge leaves out (`excludingFromBates`).
   * Undefined when the set carries no stamping or has not been read.
   */
  readonly batesSpans?: {
    readonly members: readonly BatesSpanMember[];
    readonly startNumber: number;
  };
}

/** Nothing has been read: every verdict built from this is `unknown`. */
export const UNREAD_MERGED_SET: MergedSetStaleness = {
  members: undefined,
  batesModifiedDateTime: undefined,
  watermarksModifiedDateTime: undefined,
  expectedStartById: undefined,
};

/**
 * Build the context from the two blobs a single read returns.
 *
 * `assetDetails` is `undefined` when the project carries NO redaction asset —
 * a set that has never been stamped or marked — and that is a read answer,
 * not an unread one: both stamps are then `null`, so a member carrying either
 * is correctly behind. Pass `null` for `projectDetails` only when the read
 * itself failed.
 *
 * THE PAGE COUNTS ARE THE CACHED ONES. A member whose count was never cached
 * contributes `null`, which `effectiveBatesStarts` turns into "cannot say"
 * from that member onward — the verdict reads `unknown` there rather than
 * guessing a start, which is the same answer the editor gives for a document
 * whose page count has not loaded.
 */
export const buildMergedSetStaleness = (
  projectDetails: unknown,
  assetDetails: unknown
): MergedSetStaleness => {
  const project = parseProjectDetails(projectDetails);
  if (!project) {
    return UNREAD_MERGED_SET;
  }
  const memberIds = project.members.map((member) => member.tdoId);
  const asset = parseProjectRedactionAssetDetails(assetDetails);
  const batesSpans = asset.bates
    ? {
        members: memberIds.map((tdoId: TDOId) => ({
          tdoId,
          originalPageCount: projectMemberPageCount(project, tdoId),
        })),
        startNumber: asset.bates.startNumber,
      }
    : undefined;
  return {
    members: new Set(memberIds.map(String)),
    batesModifiedDateTime: asset.batesModifiedDateTime,
    watermarksModifiedDateTime: asset.watermarksModifiedDateTime,
    // No settings means no stamping, and so no start to expect. An empty map
    // rather than undefined: the membership IS known, and `undefined` here
    // would be read as "not loaded" by a verdict that can answer.
    expectedStartById: batesSpans
      ? effectiveBatesStarts(batesSpans.members, batesSpans.startNumber)
      : new Map(),
    batesSpans,
  };
};

/**
 * The same context with the members the merge leaves out spanning no Bates
 * numbers (VE-32395; see `effectiveBatesStarts`).
 *
 * APPLIED BY THE CALLER, not at the read: which members nobody has worked on
 * is a fact about the members' own assets, which this read does not fetch.
 * The folder's rows answer it for the row marks; the download gate answers
 * it from the fresh read it makes of those assets anyway. Keeping it out of
 * this read also keeps that network call from re-running every time the
 * rows refresh.
 */
export const excludingFromBates = (
  set: MergedSetStaleness,
  excludedIds: ReadonlySet<string>
): MergedSetStaleness =>
  set.batesSpans && excludedIds.size > 0
    ? {
        ...set,
        expectedStartById: effectiveBatesStarts(
          set.batesSpans.members,
          set.batesSpans.startNumber,
          excludedIds
        ),
      }
    : set;

/**
 * The same context with the set's MARKING left unchecked.
 *
 * For the watermarks feature flag being off, where the burn draws no marks
 * and records no stamp: judging against the set would then amber every member
 * of the release with no way to clear it. `undefined` is the only honest
 * value — the check cannot run, so it says nothing either way.
 *
 * The numbering is untouched. It has no flag.
 */
export const withoutWatermarkCheck = (
  set: MergedSetStaleness
): MergedSetStaleness => ({ ...set, watermarksModifiedDateTime: undefined });
